import React from 'react';
import { 
  Droplet, 
  Calendar, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  X
} from 'lucide-react';

const ActivityTimeline = ({ donorActivity, requesterActivity }) => {
  // Combine and sort activities by date
  const combineActivities = () => {
    const activities = [];
    
    // Add donor activities
    if (donorActivity && donorActivity.length > 0) {
      donorActivity.forEach(activity => {
        activities.push({
          id: activity._id,
          type: 'donor',
          date: new Date(activity.createdAt),
          bloodType: activity.bloodType,
          patientName: activity.patientName,
          status: activity.status,
          hospital: activity.hospital?.name || 'Not specified',
          // Find the donor's status in this request
          donorStatus: activity.donors.find(d => 
            d.donor && (typeof d.donor === 'string' || d.donor._id)
          )?.status || 'accepted'
        });
      });
    }
    
    // Add requester activities
    if (requesterActivity && requesterActivity.length > 0) {
      requesterActivity.forEach(activity => {
        activities.push({
          id: activity._id,
          type: 'requester',
          date: new Date(activity.createdAt),
          bloodType: activity.bloodType,
          patientName: activity.patientName,
          status: activity.status,
          hospital: activity.hospital?.name || 'Not specified',
          donorsCount: activity.donors?.length || 0
        });
      });
    }
    
    // Sort by date (newest first)
    return activities.sort((a, b) => b.date - a.date);
  };
  
  const activities = combineActivities();
  
  if (activities.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
          <Calendar className="w-6 h-6 text-gray-400" />
        </div>
        <h4 className="mt-2 text-gray-700 font-medium">No Recent Activity</h4>
        <p className="text-gray-500 text-sm mt-1">Your activity will appear here once you start using the platform.</p>
      </div>
    );
  }
  
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getStatusIcon = (status, type) => {
    if (type === 'donor') {
      switch (status) {
        case 'accepted':
          return <Clock className="w-4 h-4 text-blue-500" />;
        case 'donated':
          return <Droplet className="w-4 h-4 text-red-500" />;
        case 'verified':
          return <CheckCircle className="w-4 h-4 text-green-500" />;
        case 'cancelled':
          return <X className="w-4 h-4 text-gray-500" />;
        default:
          return <Clock className="w-4 h-4 text-gray-500" />;
      }
    } else {
      switch (status) {
        case 'fulfilled':
          return <CheckCircle className="w-4 h-4 text-green-500" />;
        case 'active':
          return <Clock className="w-4 h-4 text-blue-500" />;
        case 'pending':
          return <Clock className="w-4 h-4 text-amber-500" />;
        case 'cancelled':
          return <X className="w-4 h-4 text-gray-500" />;
        case 'expired':
          return <AlertCircle className="w-4 h-4 text-red-500" />;
        default:
          return <Clock className="w-4 h-4 text-gray-500" />;
      }
    }
  };
  
  const getStatusText = (status, type) => {
    if (type === 'donor') {
      switch (status) {
        case 'accepted':
          return 'Accepted';
        case 'donated':
          return 'Donated';
        case 'verified':
          return 'Verified';
        case 'cancelled':
          return 'Cancelled';
        default:
          return 'Pending';
      }
    } else {
      switch (status) {
        case 'fulfilled':
          return 'Fulfilled';
        case 'active':
          return 'Active';
        case 'pending':
          return 'Pending';
        case 'cancelled':
          return 'Cancelled';
        case 'expired':
          return 'Expired';
        default:
          return 'Pending';
      }
    }
  };
  
  const getStatusColor = (status, type) => {
    if (type === 'donor') {
      switch (status) {
        case 'accepted':
          return 'bg-blue-100 text-blue-800';
        case 'donated':
          return 'bg-red-100 text-red-800';
        case 'verified':
          return 'bg-green-100 text-green-800';
        case 'cancelled':
          return 'bg-gray-100 text-gray-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    } else {
      switch (status) {
        case 'fulfilled':
          return 'bg-green-100 text-green-800';
        case 'active':
          return 'bg-blue-100 text-blue-800';
        case 'pending':
          return 'bg-amber-100 text-amber-800';
        case 'cancelled':
          return 'bg-gray-100 text-gray-800';
        case 'expired':
          return 'bg-red-100 text-red-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    }
  };
  
  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {activities.map((activity, index) => (
          <li key={activity.id || index}>
            <div className="relative pb-8">
              {index !== activities.length - 1 ? (
                <span
                  className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                  aria-hidden="true"
                />
              ) : null}
              <div className="relative flex space-x-3">
                <div>
                  <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${
                    activity.type === 'donor' ? 'bg-red-50' : 'bg-blue-50'
                  }`}>
                    {activity.type === 'donor' ? (
                      <Droplet className="h-4 w-4 text-red-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-blue-600" />
                    )}
                  </span>
                </div>
                <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                  <div>
                    <p className="text-sm text-gray-900">
                      {activity.type === 'donor' ? (
                        <>
                          You {getStatusText(activity.donorStatus, 'donor').toLowerCase()} a donation request for{' '}
                          <span className="font-medium">{activity.bloodType}</span> blood
                        </>
                      ) : (
                        <>
                          You created a blood request for{' '}
                          <span className="font-medium">{activity.bloodType}</span> blood
                        </>
                      )}
                    </p>
                    <div className="mt-1 text-xs text-gray-500 flex flex-wrap gap-2">
                      <span>Patient: {activity.patientName}</span>
                      <span>•</span>
                      <span>Hospital: {activity.hospital}</span>
                      {activity.type === 'requester' && (
                        <>
                          <span>•</span>
                          <span>Donors: {activity.donorsCount}/3</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="whitespace-nowrap text-right text-sm">
                    <div className="flex flex-col items-end gap-1">
                      <time className="text-xs text-gray-500">{formatDate(activity.date)}</time>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        getStatusColor(activity.type === 'donor' ? activity.donorStatus : activity.status, activity.type)
                      }`}>
                        {getStatusIcon(activity.type === 'donor' ? activity.donorStatus : activity.status, activity.type)}
                        <span className="ml-1">
                          {getStatusText(activity.type === 'donor' ? activity.donorStatus : activity.status, activity.type)}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ActivityTimeline;
